import { useEffect, useState } from 'react';
import { useParams } from 'react-router-dom';
import {
  getAllPersons,
  getAllStringings,
  getAllStringTypes,
} from '../api';
import {
  Person,
  StringType,
  StringingEntry,
} from '../types';
import {
  Container,
  Typography,
  Stack,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  Paper,
  Button,
  IconButton,
  Box,
  Avatar,
  TableSortLabel,
} from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import ArrowBackIcon from '@mui/icons-material/ArrowBack';
import ReactQuill from 'react-quill';
import 'react-quill/dist/quill.snow.css';
import EditForm from './EditForm';
import EditStringTypeForm from './EditStringTypeForm';

export default function PersonDetailPage() {
  const { id } = useParams<{ id: string }>();
  const [person, setPerson] = useState<Person | null>(null);
  const [stringings, setStringings] = useState<StringingEntry[]>([]);
  const [stringTypes, setStringTypes] = useState<StringType[]>([]);
  const [sortBy, setSortBy] = useState<keyof StringingEntry>('strungAt');
  const [sortOrder, setSortOrder] = useState<'asc' | 'desc'>('desc');
  const [selectedStringing, setSelectedStringing] = useState<StringingEntry | null>(null);
  const [reloadKey, setReloadKey] = useState(0);
  const [editingPreferences, setEditingPreferences] = useState(false);
  const [preferencesDraft, setPreferencesDraft] = useState('');
  const [selectedStringType, setSelectedStringType] = useState<StringType | null>(null);
  const [isEditingStringType, setIsEditingStringType] = useState(false);
  const [stringFormReadOnly, setStringFormReadOnly] = useState(true);

  useEffect(() => {
    async function fetchData() {
      const [persons, strings, stringings] = await Promise.all([
        getAllPersons(),
        getAllStringTypes(),
        getAllStringings(),
      ]);

      const foundPerson = persons.find(p => p.id === id) || null;
      const personStringings = stringings.filter(s => s.personId === id);

      setPerson(foundPerson);
      setStringTypes(strings);
      setStringings(personStringings);
      if (foundPerson) setPreferencesDraft(foundPerson.preferences || '');
    }

    if (id) fetchData();
  }, [id, reloadKey]);

  const handleSort = (field: keyof StringingEntry | 'strungAt') => {
    if (sortBy === field) {
      setSortOrder(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      setSortBy(field as keyof StringingEntry);
      setSortOrder('asc');
    }
  };

  const closeEditForm = () => {
    setReloadKey(prev => prev + 1);
    setSelectedStringing(null);
  };

  const handleSavePreferences = async () => {
    if (!id) return;
    try {
      const res = await fetch(`http://localhost:4000/api/persons/${id}`, {
        method: 'PUT',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify({ preferences: preferencesDraft }),
      });
      const updated = await res.json();
      setPerson(updated);
      setEditingPreferences(false);
    } catch (err) {
      console.error(err);
      alert('Failed to update preferences');
    }
  };

  const sortedStringings = [...stringings].sort((a, b) =>
    new Date(b.strungAt).getTime() - new Date(a.strungAt).getTime()
  );

  const uniqueRacquets = sortedStringings
    .map(s => s.racquet)
    .filter((r): r is NonNullable<typeof r> => !!r)
    .filter((r, i, arr) => arr.findIndex(r2 => r2.id === r.id) === i);

  if (!person) return <Typography>Loading...</Typography>;

  return (
    <>
      {selectedStringing ? (
        <EditForm selectedStringing={selectedStringing} closeForm={closeEditForm} />
      ) : (
        <Container>
          <Button href="/app" startIcon={<ArrowBackIcon />} sx={{ py: 3 }}>
            Back to Home
          </Button>

          {/* Profile Header */}
          <Stack direction="row" spacing={2} alignItems="center" mb={3}>
            <Avatar sx={{ width: 48, height: 48 }} />
            <Box>
              <Typography variant="h4">
                {person.firstName} {person.lastName}
              </Typography>
            </Box>
          </Stack>

          {/* Preferences Section */}
          <Box mb={4}>
            <Stack direction="row" alignItems="center" spacing={1} mb={1}>
              <Typography
                variant="subtitle2"
                sx={{ fontWeight: 600, fontSize: '0.875rem', marginTop: '0px' }}
                color="text.secondary"
              >
                Preferences
              </Typography>
              <Button
                size="small"
                onClick={() => setEditingPreferences(true)}
                startIcon={<EditIcon />}
                sx={{ minWidth: 'auto', padding: 0, color: '#00000099' }}
              />
            </Stack>
            {!editingPreferences ? (
              <Stack spacing={1}>
                {person.preferences ? (
                  <Box
                  sx={{
                    fontSize: '0.9rem',
                    lineHeight: 1.5,
                    marginBottom: 0,
                    '& p': {
                      marginBlockStart: 0,
                      marginBlockEnd: 0,
                    },
                  }}
                  dangerouslySetInnerHTML={{ __html: person.preferences }}
                />
                ) : (
                  <Typography fontStyle="italic" color="text.secondary">
                    No preferences listed
                  </Typography>
                )}
              </Stack>
            ) : (
              <Stack spacing={2}>
                <ReactQuill
                  theme="snow"
                  value={preferencesDraft}
                  onChange={setPreferencesDraft}
                  modules={{
                    toolbar: [
                      ['bold', 'italic', 'underline'],
                      [{ list: 'ordered' }, { list: 'bullet' }],
                      ['clean'],
                    ],
                  }}
                />
                <Stack direction="row" spacing={1} sx={{ justifyContent: "flex-end" }}>
                  <Button variant="text" size="small" onClick={() => setEditingPreferences(false)}>
                    Cancel
                  </Button>
                  <Button variant="contained" size="small" onClick={handleSavePreferences}>
                    Save
                  </Button>
                </Stack>
              </Stack>
            )}
          </Box>

          {/* Racquets Section */}
          <Box mb={4}>
            <Typography variant="subtitle2" sx={{ fontWeight: 600, fontSize: '0.875rem', mb: 1 }} color="text.secondary">
              Racquets
            </Typography>
            {uniqueRacquets.length > 0 ? (
              <Stack spacing={0.5}>
                {uniqueRacquets.map(r => (
                  <Typography key={r.id} variant="body2">
                    {r.brand} {r.model}
                  </Typography>
                ))}
              </Stack>
            ) : (
              <Typography fontStyle="italic" color="text.secondary">
                No racquets found.
              </Typography>
            )}
          </Box>

          {/* Stringing History Table */}
          <TableContainer component={Paper} sx={{ mt: 2 }}>
            <Table size="small">
              <TableHead>
                <TableRow>
                  <TableCell>
                    <TableSortLabel
                      active={sortBy === 'strungAt'}
                      direction={sortBy === 'strungAt' ? sortOrder : 'asc'}
                      onClick={() => handleSort('strungAt')}
                    >
                      Date
                    </TableSortLabel>
                  </TableCell>
                  <TableCell>Racquet</TableCell>
                  <TableCell>Mains</TableCell>
                  <TableCell>Tension</TableCell>
                  <TableCell>Crosses</TableCell>
                  <TableCell>Tension</TableCell>
                  <TableCell>Feedback</TableCell>
                  <TableCell />
                </TableRow>
              </TableHead>
              <TableBody>
                {sortedStringings.map(s => (
                  <TableRow
                    key={s.id}
                    hover
                    sx={{ '&:hover .edit-button': { visibility: 'visible' }, cursor: 'pointer', padding: '8px' }}
                    onClick={() => setSelectedStringing(s)}
                  >
                    <TableCell>
                      {s.strungAt
                        ? new Date(s.strungAt).toLocaleDateString(undefined, { month: 'short', day: 'numeric' })
                        : 'N/A'}
                    </TableCell>
                    <TableCell sx={{ textWrap: 'nowrap' }}>{s.racquet ? `${s.racquet.brand} ${s.racquet.model}` : 'Unknown'}</TableCell>
                    <TableCell sx={{ textWrap: 'nowrap' }}>
                      {(() => {
                        const mains = stringTypes.find(str => str.id === s.mainsId);
                        return mains ? (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation(); // prevent row click
                              setSelectedStringType(mains);
                            }}
                            sx={{ padding: 0, minWidth: 0, textTransform: 'none', lineHeight: 'unset', textAlign: 'left' }}
                          >
                            {mains.brand} {mains.name} {mains.gauge}
                          </Button>
                        ) : 'Unknown';
                      })()}
                    </TableCell>
                    <TableCell>{s.mainsTension} lbs</TableCell>
                    <TableCell sx={{ textWrap: 'nowrap' }}>
                      {(() => {
                        const crosses = stringTypes.find(str => str.id === s.crossesId);
                        return crosses ? (
                          <Button
                            onClick={(e) => {
                              e.stopPropagation(); // prevent row click
                              setSelectedStringType(crosses);
                            }}
                            sx={{ padding: 0, minWidth: 0, textTransform: 'none' }}
                          >
                            {crosses.brand} {crosses.name} {crosses.gauge}
                          </Button>
                        ) : 'Unknown';
                      })()}
                    </TableCell>
                    <TableCell>{s.crossesTension} lbs</TableCell>
                    <TableCell sx={{ minWidth: '200px' }}>{s.feedback}</TableCell>
                    <TableCell align="right">
                      <IconButton
                        className="edit-button"
                        size="small"
                        sx={{ visibility: 'hidden' }}
                      >
                        <EditIcon fontSize="small" />
                      </IconButton>
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </TableContainer>
          <EditStringTypeForm
            open={!!selectedStringType}
            onClose={() => {
              setSelectedStringType(null);
              setIsEditingStringType(false);
              setStringFormReadOnly(true);
            }}
            existing={selectedStringType}
            onUpdated={() => setReloadKey(prev => prev + 1)}
            readOnly={stringFormReadOnly}
            setReadOnly={setStringFormReadOnly}
          />
        </Container>
      )}
    </>
  );
}
